import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Accordion } from '../../../components/Accordion/Accordion';
import { Panel } from '../../../components/Panel/Panel';
import { Column, GroupedColumn } from '../types';
import { ColumnOrder } from './ColumnOrder';
import { ColumnSelection } from './ColumnSelection';

interface Props {
  columns: Column[];
  columnOrder: string[];
  mode: null | 'selection' | 'order';
  primaryColumnKey: string;
  onHide: () => void;
  onUpdateColumnOrder: (updatedColumnOrder: string[]) => void;
  groupedColumns?: GroupedColumn[];
}

export const ManageColumnsPanel = ({
  columns,
  columnOrder: initialColumnOrder,
  groupedColumns,
  mode,
  primaryColumnKey,
  onHide,
  onUpdateColumnOrder,
}: Props) => {
  const intl = useIntl();
  const [draftColumnOrder, setDraftColumnOrder] = useState(initialColumnOrder);

  useEffect(() => {
    setDraftColumnOrder(initialColumnOrder);
  }, [initialColumnOrder]);

  const isDirty = !isEqual(initialColumnOrder, draftColumnOrder);

  const handleHide = () => {
    setDraftColumnOrder(initialColumnOrder);
    onHide();
  };

  const handleUpdate = () => {
    onUpdateColumnOrder(draftColumnOrder);
    onHide();
  };

  const accordionItems = [
    {
      title: intl.formatMessage({
        id: 'Table.manageColumnsPanel.columnSelection',
        defaultMessage: 'Column selection',
        description:
          'Title for the column selection section in the manage columns panel',
      }),
      content: (
        <ColumnSelection
          columns={columns}
          columnOrder={draftColumnOrder}
          groupedColumns={groupedColumns}
          primaryColumnKey={primaryColumnKey}
          onUpdateColumnOrder={setDraftColumnOrder}
        />
      ),
      isExpanded: mode === 'selection',
    },
    {
      title: intl.formatMessage({
        id: 'Table.manageColumnsPanel.columnOrder',
        defaultMessage: 'Column order',
        description:
          'Title for the column order section in the manage columns panel',
      }),
      content: (
        <ColumnOrder
          columns={columns}
          columnOrder={draftColumnOrder}
          primaryColumnKey={primaryColumnKey}
          onUpdateColumnOrder={setDraftColumnOrder}
        />
      ),
      isExpanded: mode === 'order',
    },
  ];

  return mode !== null ? (
    <Panel
      footer={{
        actions: [
          {
            text: intl.formatMessage({
              id: 'Table.manageColumnsPanel.cancel',
              defaultMessage: 'Cancel',
              description:
                'Text for the cancel button in the manage columns panel',
            }),
            level: 'tertiary' as const,
            onClick: handleHide,
          },
          {
            text: intl.formatMessage({
              id: 'Table.manageColumnsPanel.update',
              defaultMessage: 'Update',
              description:
                'Text for the update button in the manage columns panel',
            }),
            level: 'primary' as const,
            onClick: handleUpdate,
            disabled: !isDirty,
          },
        ],
      }}
      hidden={{
        onHide: handleHide,
      }}
      width="l"
      mode="floating"
      enableBackdrop
      title={intl.formatMessage({
        id: 'Table.manageColumnsPanel.title',
        defaultMessage: 'Manage columns',
        description: 'Title for the manage columns panel',
      })}
      enableContentPadding={false}
    >
      <Accordion items={accordionItems} />
    </Panel>
  ) : null;
};
